import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ParteProduccion, ParteProduccionState } from '../types';
import { ParteProduccionService } from '../services/ParteProduccionService';

interface ParteProduccionActions {
  fetchPartes: (page?: number, pageSize?: number) => Promise<void>;
  fetchParteById: (id: number) => Promise<ParteProduccion | null>;
  createParte: (data: any) => Promise<ParteProduccion | null>;
  updateParte: (id: number, data: any) => Promise<ParteProduccion | null>;
  deleteParte: (id: number) => Promise<boolean>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  clearError: () => void;
  reset: () => void;
}

type ParteProduccionStore = ParteProduccionState & ParteProduccionActions;

const initialState: ParteProduccionState = {
  partes: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
};

export const useParteProduccionStore = create<ParteProduccionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchPartes: async (page, pageSize) => {
        const { currentPage, pageSize: currentPageSize } = get();
        const targetPage = page ?? currentPage;
        const targetPageSize = pageSize ?? currentPageSize;

        set({ loading: true, error: null });

        try {
          const response = await ParteProduccionService.getAll(targetPage, targetPageSize);
          set({
            partes: response.data,
            totalCount: response.totalCount,
            currentPage: targetPage,
            pageSize: targetPageSize,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar los partes de producción',
            loading: false,
          });
        }
      },

      fetchParteById: async (id: number) => {
        set({ loading: true, error: null });

        try {
          const parte = await ParteProduccionService.getById(id);
          set({ loading: false });
          return parte;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar el parte de producción',
            loading: false,
          });
          return null;
        }
      },

      createParte: async (data) => {
        set({ loading: true, error: null });

        try {
          const newParte = await ParteProduccionService.create(data);
          const { fetchPartes } = get();
          await fetchPartes();
          set({ loading: false });
          return newParte;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al crear el parte de producción',
            loading: false,
          });
          return null;
        }
      },

      updateParte: async (id: number, data) => {
        set({ loading: true, error: null });

        try {
          const updatedParte = await ParteProduccionService.update(id, data);
          const { fetchPartes } = get();
          await fetchPartes();
          set({ loading: false });
          return updatedParte;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar el parte de producción',
            loading: false,
          });
          return null;
        }
      },

      deleteParte: async (id: number) => {
        set({ loading: true, error: null });

        try {
          await ParteProduccionService.delete(id);
          const { fetchPartes } = get();
          await fetchPartes();
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar el parte de producción',
            loading: false,
          });
          return false;
        }
      },

      setPage: (page: number) => {
        set({ currentPage: page });
      },

      setPageSize: (pageSize: number) => {
        set({ pageSize, currentPage: 1 });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'parte-produccion-store' }
  )
);